import type { MutationFunctionContext } from '@tanstack/react-query';

import config from 'config';
import { createAPIError } from 'utils/exceptions/apiError';

import { readChallengeHash } from './otp';

export type PlanOffer = {
  visitsToReachCap: number;
  pricePerVisit: number;
  planName: string;
  planId: string;
  planCurrency: string;
  maxCostPerUsage: number;
  calculatedPricePerVisit: number;
  freeVisitsPerUser?: number;
};

export type OfferDecision =
  | 'no_decision'
  | 'accepted_budget'
  | 'accepted_best_value'
  | 'payment_is_too_low'
  | 'unclear_proposal'
  | 'know_more'
  | 'other';

export type Offer = {
  offerId: string;
  offerDecision: OfferDecision;
  additionalReasonInfo?: string;
  budget: PlanOffer;
  bestValue?: Partial<PlanOffer>;
};

type PostOfferVariables = {
  leadId: string;
  /** The email whose challenge holds the hash the request sends. */
  businessEmail: string;
};

/**
 * Reads the hash of the `businessEmail` challenge when it runs, so it is a
 * `mutationFn` that takes the query client from its context.
 */
export async function postOffer(
  { leadId, businessEmail }: PostOfferVariables,
  { client }: MutationFunctionContext,
): Promise<Offer> {
  const url = `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/v1/offers`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      leadId,
      emailValidationId: readChallengeHash({
        queryClient: client,
        email: businessEmail,
      }),
    }),
  });

  if (!response.ok) {
    throw await createAPIError(response);
  }

  const result: Offer = await response.json();

  return result;
}
